<script>
/**
 * ChatChart
 *
 * Renders a Chart.js chart from a JSON spec embedded in a fenced code block.
 *
 * Chart block convention (AI prompt target):
 *
 *   ```chart
 *   {
 *     "type": "bar",
 *     "title": "Top 5 US Cities by Population",
 *     "labels": ["New York", "Los Angeles", "Chicago", "Houston", "Phoenix"],
 *     "datasets": [
 *       {
 *         "label": "Population (millions)",
 *         "data": [8.3, 3.9, 2.7, 2.3, 1.6]
 *       }
 *     ]
 *   }
 *   ```
 *
 * Supported types: bar, line, pie, doughnut (Chart.js built-ins).
 * The spec is parsed and validated here; malformed JSON shows an error card
 * rather than crashing the whole message.
 */
import { Chart, registerables } from 'chart.js'
Chart.register(...registerables)

export default {
  name: 'ChatChart',

  props: {
    spec: {
      type: String,
      required: true,
    },
  },

  data() {
    // Parse once up front rather than in a computed so we avoid
    // mutating data from inside a computed property (eslint vue/no-side-effects-in-computed-properties).
    let parsed = null
    let parseError = null
    try {
      parsed = JSON.parse(this.spec)
    } catch (_) {
      parseError = 'Chart spec is not valid JSON.'
    }
    return {
      parsedSpec: parsed,
      parseError,
      chartTitle: parsed && parsed.title ? parsed.title : null,
      chartInstance: null,
    }
  },

  mounted() {
    if (this.parsedSpec) {
      this.renderChart()
    }
  },

  beforeUnmount() {
    this.destroyChart()
  },

  methods: {
    renderChart() {
      this.destroyChart()
      const canvas = this.$refs.canvas
      if (!canvas) return

      const spec = this.parsedSpec
      const type = spec.type || 'bar'
      const labels = spec.labels || []
      const rawDatasets = Array.isArray(spec.datasets) ? spec.datasets : []

      // Inject a consistent palette when the spec omits colors.
      // Each dataset cycles through the palette independently.
      const palette = [
        'rgba(33, 158, 188, 0.75)',
        'rgba(33, 158, 188, 0.5)',
        'rgba(142, 202, 230, 0.75)',
        'rgba(142, 202, 230, 0.5)',
        'rgba(8, 120, 149, 0.75)',
        'rgba(8, 120, 149, 0.5)',
      ]
      const borderPalette = [
        'rgba(33, 158, 188, 1)',
        'rgba(33, 158, 188, 0.85)',
        'rgba(142, 202, 230, 1)',
        'rgba(142, 202, 230, 0.85)',
        'rgba(8, 120, 149, 1)',
        'rgba(8, 120, 149, 0.85)',
      ]

      const isPie = type === 'pie' || type === 'doughnut'

      const datasets = rawDatasets.map((ds, i) => {
        const base = { ...ds }
        if (!base.backgroundColor) {
          base.backgroundColor = isPie
            ? labels.map((_, j) => palette[j % palette.length])
            : palette[i % palette.length]
        }
        if (!base.borderColor) {
          base.borderColor = isPie
            ? labels.map((_, j) => borderPalette[j % borderPalette.length])
            : borderPalette[i % borderPalette.length]
        }
        if (!('borderWidth' in base)) {
          base.borderWidth = 1.5
        }
        if (type === 'line' && !('tension' in base)) {
          base.tension = 0.35
        }
        return base
      })

      this.chartInstance = new Chart(canvas, {
        type,
        data: { labels, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: datasets.length > 1 || isPie,
              labels: {
                color: 'var(--text-primary, #1c1c1c)',
                font: { size: 12 },
              },
            },
            title: {
              display: false, // handled by the template heading
            },
            tooltip: {
              backgroundColor: 'rgba(0,0,0,0.75)',
              titleColor: '#fff',
              bodyColor: '#fff',
              cornerRadius: 6,
              padding: 8,
            },
          },
          scales: isPie
            ? {}
            : {
                x: {
                  ticks: { color: 'var(--text-secondary, #444)', font: { size: 11 } },
                  grid: { color: 'rgba(0,0,0,0.06)' },
                },
                y: {
                  ticks: { color: 'var(--text-secondary, #444)', font: { size: 11 } },
                  grid: { color: 'rgba(0,0,0,0.06)' },
                  beginAtZero: true,
                },
              },
        },
      })
    },

    destroyChart() {
      if (this.chartInstance) {
        this.chartInstance.destroy()
        this.chartInstance = null
      }
    },
  },
}
</script>

<template>
  <div class="chat-chart">
    <div v-if="parseError" class="chat-chart__error" role="alert">
      <span>Chart could not be rendered: {{ parseError }}</span>
    </div>
    <template v-else>
      <p v-if="chartTitle" class="chat-chart__title">{{ chartTitle }}</p>
      <div class="chat-chart__canvas-wrapper">
        <canvas ref="canvas"></canvas>
      </div>
    </template>
  </div>
</template>

<style scoped>
.chat-chart {
  margin: 1.25rem 0;
  border: 1px solid var(--border-subtle, rgba(0, 0, 0, 0.08));
  border-radius: var(--radius-lg, 12px);
  padding: 1rem 1.25rem 1.25rem;
  background: var(--bg-surface, #ffffff);
  box-shadow: var(--shadow-card, 0 1px 3px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.04));
}

.chat-chart__title {
  margin: 0 0 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary, #1c1c1c);
  line-height: 1.4;
}

.chat-chart__canvas-wrapper {
  position: relative;
  height: 280px;
}

.chat-chart__canvas-wrapper canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
}

.chat-chart__error {
  padding: 0.75rem 1rem;
  border-radius: var(--radius-md, 8px);
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-size: 0.8125rem;
}
</style>
